import { useState, useRef, useEffect } from "react";
import { Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import { toEventDate, formatEventDate } from "@shared/event-date";

interface SpreadsheetCellProps {
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  isEditing: boolean;
  onStartEdit: () => void;
  onEndEdit: () => void;
  onTab?: (shiftKey: boolean) => void;
  className?: string;
  type?: "text" | "date" | "time" | "phone" | "email";
  placeholder?: string;
  // Wave 3 — click-to-dial: when type="phone" and value is non-empty,
  // show a phone-icon button that dispatches `entertaincrm:dialer:open`
  // to surface the global mini-dialer overlay (DialerHost in App.tsx).
  dialContext?: { leadId?: string | null; clientId?: string | null; contactName?: string | null };
  // When false the dial button is hidden (e.g. Twilio not connected). Defaults to true.
  showDial?: boolean;
}

export function SpreadsheetCell({
  value,
  onChange,
  onFocus,
  isEditing,
  onStartEdit,
  onEndEdit,
  onTab,
  className,
  type = "text",
  placeholder = "",
  dialContext,
  showDial = true,
}: SpreadsheetCellProps) {
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Task #112 — event dates are canonical YYYY-MM-DD strings. Use the
  // shared helper so we don't re-introduce timezone drift by funneling
  // the value through `new Date(...)`.
  const formatForInput = (val: string) => {
    if (!val) return "";
    if (type === "date") {
      return toEventDate(val) ?? val;
    }
    return val;
  };

  useEffect(() => {
    setLocalValue(formatForInput(value));
  }, [value, type]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onStartEdit();
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onFocus) {
      onFocus();
    }
    // Single-click to edit
    onStartEdit();
  };

  const handleBlur = () => {
    if (localValue !== formatForInput(value)) {
      // Task #112 — save the raw `YYYY-MM-DD` (no `T12:00:00`
      // workaround). The server column is now `date`, so the calendar
      // day round-trips unchanged through every surface.
      onChange(localValue);
    }
    onEndEdit();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleBlur();
    }
    if (e.key === "Escape") {
      setLocalValue(formatForInput(value));
      onEndEdit();
    }
    if (e.key === "Tab") {
      e.preventDefault();
      // Save current value — Task #112: dates are raw YYYY-MM-DD.
      if (localValue !== formatForInput(value)) {
        onChange(localValue);
      }
      onEndEdit();
      // Move to next/previous cell
      if (onTab) {
        onTab(e.shiftKey);
      }
    }
  };

  const formatPhoneNumber = (phone: string) => {
    // Clean the phone number: trim, remove invisible chars, extract only digits
    const cleaned = String(phone).trim().replace(/[^\d]/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    if (cleaned.length === 11 && cleaned[0] === "1") {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    if (cleaned.length === 7) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    }
    return phone;
  };

  const formatDisplayValue = (val: string) => {
    if (!val) return "";
    if (type === "date" && val) {
      // Task #112 — render the canonical YYYY-MM-DD directly via the
      // shared formatter. Forcing a timezone here (or going through
      // `new Date()`) is exactly what caused 5/19 to read as 5/18.
      const formatted = formatEventDate(val, { variant: "short" });
      return formatted || val;
    }
    if (type === "phone" && val) {
      return formatPhoneNumber(val);
    }
    return val;
  };

  return (
    <div
      className={cn(
        "spreadsheet-cell h-7 flex items-center cursor-text relative",
        isEditing && "z-50",
        className
      )}
      onDoubleClick={handleDoubleClick}
      onClick={handleClick}
      data-testid="spreadsheet-cell"
    >
      {isEditing ? (
        <input
          ref={inputRef}
          type={type === "date" ? "date" : type === "time" ? "time" : "text"}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="absolute inset-x-0 top-0 min-h-10 bg-background outline-none text-sm px-2 py-2 ring-2 ring-primary rounded-md shadow-lg"
          data-testid="input-cell-edit"
        />
      ) : (
        <div className="flex items-center gap-1 w-full px-2">
          <span className={cn("text-sm truncate flex-1", !value && "text-muted-foreground")}>
            {formatDisplayValue(value) || placeholder}
          </span>
          {type === "phone" && value && showDial && (
            <button
              type="button"
              aria-label="Call this number"
              title="Call"
              onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(
                  new CustomEvent("entertaincrm:dialer:open", {
                    detail: {
                      phone: value,
                      contactName: dialContext?.contactName ?? null,
                      leadId: dialContext?.leadId ?? null,
                      clientId: dialContext?.clientId ?? null,
                    },
                  }),
                );
              }}
              className="opacity-60 hover:opacity-100 hover:text-primary shrink-0 p-0.5 rounded"
              data-testid="button-cell-dial"
            >
              <Phone className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
